import { ChangeDetectionStrategy, Component, OnDestroy, OnInit } from '@angular/core';
import { MatChipInputEvent as MatChipInputEvent, MatChipGrid, MatChipRow, MatChipRemove, MatChipInput } from '@angular/material/chips';
import { COMMA, ENTER } from '@angular/cdk/keycodes';
import { takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs';
import { sendToGoogleAnalytics } from '../../../../core/utilities';
import { Store } from '@ngrx/store';
import * as ccListSelectors from '../state/cc-list-selectors';
import * as locationListSelectors from '../../location-list/state/location-list-selectors';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { CCActionBarActions } from '../state/cc-list-actions';
import { GxInputDirective } from '../../../../directives/gxinput.directive';
import { MatInput } from '@angular/material/input';
import { MatIcon } from '@angular/material/icon';
import { NgClass } from '@angular/common';
import { MatFormField } from '@angular/material/form-field';

// TODO: rework this like radius - updaters should be effects!

@Component({
    selector: 'app-cc-keywords-bar',
    templateUrl: './cc-keywords-bar.component.html',
    styleUrls: ['./cc-keywords-bar.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: true,
    imports: [
        MatFormField,
        MatChipGrid,
        FormsModule,
        ReactiveFormsModule,
        MatChipRow,
        NgClass,
        MatIcon,
        MatChipRemove,
        MatInput,
        MatChipInput,
        GxInputDirective,
    ],
})
export class CcKeywordsBarComponent implements OnInit, OnDestroy {

  // Claim and clean menu
  claimCleanKeywords: string[];
  // used just for enabling/disabling chipGrid
  claimCleanCtrl = new FormControl('');
  focusedLocation$ = this.store.select(locationListSelectors.selectFocusedLocation);
  readonly claimCleanKeywordSeparators: number[] = [ENTER, COMMA];

  private componentDestroyed = new Subject<void>();

  constructor(private store: Store) {
  }

  ngOnInit() {
    this.initClaimCleanKeywords();
  }

  ngOnDestroy() {
    this.componentDestroyed.next();
  }

  /**
   * Adds a keyword to the claimCleanKeywords (if is is not already included).
   * @param event MatChipInputEvent
   */
  addClaimCleanKeyword(event: MatChipInputEvent) {
    sendToGoogleAnalytics('C&C actions',
        'Add keywords',
        'Add keyword');

    const value = (event.value || '').trim();
    if (!this.claimCleanKeywords) {
      this.claimCleanKeywords = [];
    }
    if (value && !this.claimCleanKeywords.includes(value)) {
      this.claimCleanKeywords.push(value);
      this.updateClaimCleanKeywords();
    }
    event.input.value = '';
  }

  /**
   * Removes a keyword from the claimCleanKeywords (if it contains this keyword).
   * @param keyword Keyword to remove
   */
  removeClaimCleanKeyword(keyword: string) {
    sendToGoogleAnalytics('C&C actions',
        'Remove keywords',
        'Remove keyword');

    if (this.claimCleanKeywords) {
      const index = this.claimCleanKeywords.indexOf(keyword);
      if (index >= 0) {
        this.claimCleanKeywords.splice(index, 1);
        this.updateClaimCleanKeywords();
      }
    }
  }

  /**
   * Interprets the data that was pasted into the claimAndClean-chiplist and adds those values
   * to the claimCleanKeywords.
   * @param event Paste event
   */
  pasteClaimCleanKeywords(event: ClipboardEvent) {
    event.preventDefault();

    sendToGoogleAnalytics('C&C actions',
        'Add keywords',
        'Paste keywords');

    const pastedKeywords: string[] = event.clipboardData
        .getData('Text') //Gets the pasted text
        .split(/[,\n]+/) //Splits it on (one or multiple) commas or newlines
        .map(keyword => keyword.trim()) //trim
        .filter(keyword => keyword.length && !this.claimCleanKeywords?.includes(keyword));

    if (pastedKeywords.length) {
      if (!this.claimCleanKeywords) {
        this.claimCleanKeywords = [];
      }
      pastedKeywords.forEach(keyword => this.claimCleanKeywords.push(keyword));
      this.updateClaimCleanKeywords();
    }
  }

  /**
   * Fetches the claim-and-clean keywords saved on account-level and initializes
   * claimCleanKeywords with those.
   */
  initClaimCleanKeywords() {
    this.store.select(ccListSelectors.selectCcKeywords)
        .pipe(takeUntil(this.componentDestroyed))
        .subscribe((keywords) => {
      this.claimCleanKeywords = keywords ? [...keywords] : keywords;
    });

    this.focusedLocation$
        .pipe(takeUntil(this.componentDestroyed))
        .subscribe((focussedLocation) => {
          !!focussedLocation ? this.claimCleanCtrl.enable() : this.claimCleanCtrl.disable();
        });
  }

  /**
   * Updates the claim-and-clean keywords saved on account-level
   */
  updateClaimCleanKeywords() {
    this.store.dispatch(CCActionBarActions.updateCCKeywords({ ccKeywords: this.claimCleanKeywords }));
  }

}
